import { useEffect, useMemo, useState } from 'preact/hooks';
import { useTranslation } from '../i18n';
import { SectionHeading, Eyebrow, Skeleton } from '../primitives';
import { fetchProviders } from '../auth/providers';
import type { ProviderInfo } from '../auth/types';
import { EmporiumNav } from '../components/EmporiumNav';
import { Colophon } from '../components/Colophon';
import { LINKS } from '../brand';
import { versionedUrl } from '../utils/versionedUrl';

type State =
    | { status: 'loading' }
    | { status: 'success'; providers: ProviderInfo[]; recommendedId: string | null }
    | { status: 'error'; error: Error };

/**
 * Sign-in page. Fetches the enabled OAuth providers from <c>/.well-known/auth/providers</c>,
 * renders one pill button per provider, badges Discord as "Recommended" (primary identity for
 * the FFXIV community), and falls back to a guest-catalog CTA when no providers are reachable.
 *
 * Returns the user to <c>?return=...</c> after the OAuth handshake (or to the catalog if no
 * return URL was supplied). The challenge link is clicked directly via
 * <c>window.location.assign</c> so preact-iso's router doesn't intercept the navigation and
 * route it into the SPA's 404 fallback.
 */
export function SignInPage() {
    const t = useTranslation();
    const [state, setState] = useState<State>({ status: 'loading' });
    const [clickedId, setClickedId] = useState<string | null>(null);

    const returnUrl = useMemo(() => {
        if (typeof window === 'undefined') return '/';
        const params = new URLSearchParams(window.location.search);
        return params.get('return') ?? '/';
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetchProviders()
            .then((enabled) => {
                if (cancelled) return;
                const recommendedId =
                    enabled.find((p) => p.id.toLowerCase() === 'discord')?.id ?? enabled[0]?.id ?? null;
                setState({ status: 'success', providers: enabled, recommendedId });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setState({
                    status: 'error',
                    error: err instanceof Error ? err : new Error(String(err)),
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const isUnavailable =
        state.status === 'error' ||
        (state.status === 'success' && state.providers.length === 0);

    return (
        <div class="flex min-h-full flex-col">
            <EmporiumNav current={null} />
            <main class="flex-1">
                <div class="mx-auto max-w-[460px] px-[clamp(20px,4vw,40px)] py-[clamp(28px,4vw,64px)]">
                    <article class="rounded-[14px] border border-rule bg-surface-raised p-[clamp(28px,4vw,44px)] shadow-[0_24px_60px_-32px_rgba(0,0,0,0.6)]">
                        <div class="mb-6 flex flex-col items-center gap-2.5">
                            <img
                                src={versionedUrl('/loporrit-mark-192.png')}
                                alt=""
                                width="72"
                                height="72"
                                class="block h-[72px] w-[72px] object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.45)]"
                            />
                            <Eyebrow tone="faint">{t('auth.hello')}</Eyebrow>
                        </div>

                        <SectionHeading
                            title={isUnavailable ? t('auth.unavailableTitle') : t('auth.signInTitle')}
                            subtitle={isUnavailable ? t('auth.unavailableLede') : t('auth.signInLede')}
                            size="lg"
                            align="center"
                            as="h1"
                            class="mb-6"
                        />

                        <div class="min-h-[132px]" aria-label="Sign-in providers" role="group">
                            {state.status === 'loading' && (
                                <div class="flex flex-col gap-3">
                                    <Skeleton.Line height="50px" class="rounded-[10px]" />
                                    <Skeleton.Line height="50px" class="rounded-[10px]" />
                                </div>
                            )}
                            {state.status === 'success' && state.providers.length > 0 && (
                                <div class="flex flex-col gap-3">
                                    {state.providers.map((p) => {
                                        const recommended = p.id === state.recommendedId;
                                        const sep = p.challengeUrl.indexOf('?') === -1 ? '?' : '&';
                                        const challengeHref = `${p.challengeUrl}${sep}return=${encodeURIComponent(returnUrl)}`;
                                        const isClicked = clickedId === p.id;
                                        return (
                                            <a
                                                key={p.id}
                                                href={challengeHref}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setClickedId(p.id);
                                                    window.location.assign(challengeHref);
                                                }}
                                                class={`group relative inline-flex items-center gap-3.5 rounded-[10px] border border-rule-strong bg-surface-base px-4 py-3.5 text-left text-[14.5px] font-medium text-fg-primary transition-colors hover:-translate-y-px hover:text-fg-primary ${
                                                    isClicked ? 'pointer-events-none border-accent text-fg-muted' : ''
                                                }`}
                                            >
                                                <span class="flex-1">
                                                    {t('auth.continueWith', { provider: p.name || p.id })}
                                                    {isClicked && (
                                                        <span class="ml-2 inline-block h-2 w-2 animate-pulse rounded-full bg-accent align-middle" />
                                                    )}
                                                </span>
                                                {recommended && (
                                                    <span class="rounded-full border border-accent-soft bg-accent-glow px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.14em] text-accent">
                                                        {t('auth.recommended')}
                                                    </span>
                                                )}
                                                <span class="font-mono text-[14px] text-fg-faint transition-colors group-hover:translate-x-[3px] group-hover:text-accent">
                                                    →
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {!isUnavailable && (
                            <div class="my-5 flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-faint before:h-px before:flex-1 before:bg-rule before:content-[''] after:h-px after:flex-1 after:bg-rule after:content-['']">
                                {t('auth.or')}
                            </div>
                        )}

                        <a
                            href="/emporium"
                            class={
                                isUnavailable
                                    ? 'mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-accent-soft bg-accent-glow px-4 py-3 text-[14px] font-medium text-accent transition-colors hover:bg-accent hover:text-surface-base'
                                    : 'inline-flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-rule-strong px-4 py-2.5 text-[13px] text-fg-muted transition-colors hover:border-fg-muted hover:text-fg-primary'
                            }
                        >
                            {t('auth.browseAsGuest')}
                            {isUnavailable && <span aria-hidden="true" class="font-mono">→</span>}
                        </a>

                        {!isUnavailable && (
                            <p class="mt-6 border-t border-rule pt-4 text-center text-[12px] leading-[1.55] text-fg-faint">
                                {t('auth.signInLegal')}
                            </p>
                        )}

                        {state.status === 'error' && (
                            <details class="mt-6 border-t border-rule pt-3 text-[11px] leading-[1.5] text-fg-faint [&_summary]:list-none">
                                <summary class="inline-flex cursor-pointer select-none items-center gap-1.5 font-mono uppercase tracking-[0.14em] text-fg-faint hover:text-fg-muted [&::-webkit-details-marker]:hidden">
                                    <span>{t('auth.providerLoadError')}</span>
                                    <span class="font-display text-[14px] not-uppercase italic text-accent">+</span>
                                </summary>
                                <p class="mt-2 break-words font-mono text-[10.5px] text-fg-faint">
                                    {state.error.message}
                                </p>
                            </details>
                        )}
                    </article>
                </div>
            </main>
            <Colophon emporiumHref="/emporium" studioHref="/studio" packHref={LINKS.github.releasesLatest} />
        </div>
    );
}
